import analytics, { IdentifyTraits, isTrackedMarketingProduct, shouldTrackMarketingEvent } from '.';
import AppPaths from '../../AppPaths';
import { getTenancy } from '../../hooks/useTenancy';
import { hasTargetingConsent } from '../consent';
import { typedWindow } from '../Window';
import { getProductsAndVariantsBySku } from '../product';
import store from '../reduxStore';
import {
  ECOM_EVENT_CHECKOUT_STARTED,
  ECOM_EVENT_SHIPPING_COMPLETED,
  ECOM_EVENT_ORDER_COMPLETED,
  ECOM_EVENT_PAYMENT_INFO_ENTERED,
  ECOM_EVENT_PRODUCT_ADDED,
  ECOM_EVENT_PRODUCT_VIEWED,
  SegmentProduct,
  SegmentCartEventProperties,
  SegmentPurchaseEventProperties,
} from './events';

/**
 * Facebook Pixel integration for e-commerce events containing tracked collar kits (Series 3, Series 3+, Mini, Ultra).
 * Only fires events for cart/checkout actions that contain a collar kit. Interacts directly with the Facebook Pixel
 * SDK (loaded in index.html) rather than going through the Segment analytics.js integration, so we can filter events
 * by product and use trackSingle to target only this pixel ID.
 */
const FB_S3_COLLAR_PIXEL_ID = process.env.REACT_APP_FB_S3_COLLAR_PIXEL_ID || '';

// Free trial checkouts report as a single distinct content_id (rather than the collar's family name)
// so Meta can attribute and optimize them as their own product line with its own CAC.
const FB_FREE_TRIAL_CONTENT_ID = 'Free Trial';

// Map standard Segment e-commerce event names to a corresponding method that will fire Facebook Pixel events
type EcommerceEventTrack = (messageId: string, properties: any) => void;
const FB_ECOMMERCE_EVENT_MAP: Record<string, EcommerceEventTrack> = {
  [ECOM_EVENT_PRODUCT_VIEWED]: fbProductViewed,
  [ECOM_EVENT_PRODUCT_ADDED]: fbProductAdded,
  [ECOM_EVENT_CHECKOUT_STARTED]: fbCheckoutStarted,
  [ECOM_EVENT_SHIPPING_COMPLETED]: fbShippingCompleted,
  [ECOM_EVENT_PAYMENT_INFO_ENTERED]: fbPaymentInfoEntered,
  [ECOM_EVENT_ORDER_COMPLETED]: fbOrderCompleted,
};

interface FbEventProperties {
  content_ids?: string[];
  content_type?: string;
  content_name?: string;
  currency?: string;
  value?: string;
  contents?: { id: string; quantity: number; item_price: string }[];
  num_items?: number;
  // order_id enables conversion attribution and deduplication in Facebook Events Manager
  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/custom-data#order-id
  order_id?: string;
}

function fbPixel(...args: any[]) {
  // In production, use the Facebook Pixel SDK. In development, do nothing.
  const tenancy = getTenancy();
  const fbq =
    (tenancy !== 'test' &&
      process.env.REACT_APP_ENVIRONMENT === 'production' &&
      FB_S3_COLLAR_PIXEL_ID &&
      typedWindow.fbq) ||
    (() => {
      // Development environment. Do nothing
    });

  fbq(...args);
}

// Ported from our forked analytics.js library
// https://github.com/barkinglabs/analytics.js/blob/f0a20a8d7396fb5a255e0088a0942adbd8a281ba/analytics.js#L5760-L5808
function formatTraits(traits: IdentifyTraits) {
  const email = traits.email?.toLowerCase();

  let firstName: string | undefined = undefined;
  let lastName: string | undefined = undefined;

  // Check for firstName property
  // else check for name
  if (traits.firstName) {
    firstName = traits.firstName;
    lastName = traits.lastName;
  } else {
    const nameArray = (traits.name && traits.name.toLowerCase().split(' ')) || [];
    firstName = nameArray.shift();
    lastName = nameArray.pop();
  }

  // Gender as m or f
  let gender: string | undefined = undefined;
  if (traits.gender && typeof traits.gender === 'string') {
    gender = traits.gender.slice(0, 1).toLowerCase();
  }

  // Format birthday as yyyymmdd
  const birthday = traits.birthday && traits.birthday.toISOString().slice(0, 10).split('-').join('');
  const address = traits.address || {};
  const city = address.city && address.city.split(' ').join('').toLowerCase();
  const state = address.state && address.state.toLowerCase();
  const postalCode = address.postalCode;

  const external_id = analytics?.userOrAnonymousId() || undefined;

  const fbTraits: Record<string, string | undefined> = {
    em: email,
    fn: firstName,
    ln: lastName,
    ph: traits.phone,
    ge: gender,
    db: birthday,
    ct: city,
    st: state,
    zp: postalCode,
    external_id,
  };

  // Remove all undefined values
  Object.keys(fbTraits).forEach((key) => fbTraits[key] === undefined && delete fbTraits[key]);

  return fbTraits;
}

/**
 * Consent is re-read per event, not just at load. index.html only gates whether fbevents.js is
 * fetched, so a visitor who consents and then withdraws mid-session still has a live SDK — the
 * other marketing pixels (agentio, vibe) check at fire time for the same reason.
 */
function onTrack(event: string, properties: any, messageId: string) {
  const mappedEvent = FB_ECOMMERCE_EVENT_MAP[event];
  if (!mappedEvent || !hasTargetingConsent() || !shouldTrackMarketingEvent(properties)) {
    return;
  }
  // Re-init the pixel with current user traits so advanced matching data
  // is available for the immediately following trackSingle call.
  refreshAdvancedMatching();
  mappedEvent(messageId, properties);
}

/**
 * PageView is fired here rather than from a tag manager so that it carries an eventID. Meta's
 * managed Conversions API replays every browser pixel event server-side and copies whatever
 * eventID the call carried — without one, the replay can't be deduplicated and each page view is
 * counted twice.
 */
function onPage(messageId: string) {
  // Verizon checkout is excluded from marketing pixels, matching shouldTrackMarketingEvent.
  const isVerizonCheckout = typeof window !== 'undefined' && window.location.pathname.includes(AppPaths.Verizon.Root);
  if (isVerizonCheckout || !hasTargetingConsent()) {
    return;
  }
  refreshAdvancedMatching();
  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'PageView', {}, { eventID: messageId });
}

function onIdentify(_userId: string, identifyTraits?: IdentifyTraits, _options?: SegmentAnalytics.SegmentOpts) {
  const traits = formatTraits(identifyTraits ?? {});
  const fbq = typedWindow.fbq;
  const pixel = fbq?.instance?.pixelsByID?.[FB_S3_COLLAR_PIXEL_ID];
  if (pixel) {
    pixel.userData = { ...pixel.userData, ...traits };
  }
}

let callbacksRegistered = false;

export function fbSetupCallbacks() {
  // Registering twice would attach a second analytics.js 'invoke' listener and fire every pixel
  // event twice — SegmentAnalytics builds a fresh closure per call, so its off() has nothing to
  // match. Guarded here rather than there because onTrack has several legitimate subscribers
  // (northbeam, vibe, agentio) that each need their own listener.
  if (callbacksRegistered) {
    return;
  }
  callbacksRegistered = true;

  // To fire the events to the alternate Facebook pixel, we'll use the callbacks from Segment's analytics.js
  // library. This way, we can get the exact same event, properties, and options that were used to generate
  // the message sent to our analytics backend and other analytics.js integrations.
  analytics.onTrack(onTrack);
  analytics.onIdentify(onIdentify);
  analytics.onPage(onPage);
}

export function fbInit() {
  const segmentTraits = analytics.userTraits();
  const existingTraits =
    Object.keys(segmentTraits).length > 0
      ? formatTraits(segmentTraits)
      : { external_id: analytics.userOrAnonymousId() };

  fbPixel('init', FB_S3_COLLAR_PIXEL_ID, existingTraits);
}

/**
 * Update the pixel's internal userData directly before firing a track event.
 *
 * The FB pixel SDK only honors user data from the first non-empty `init` call per pixel ID.
 * In our SPA, the first non-empty init fires early (via fbInit) with just external_id —
 * before user PII is available. Subsequent init calls with email/name/phone are silently
 * ignored by the SDK.
 *
 * Workaround: write directly to the pixel's internal userData store at
 * `window.fbq.instance.pixelsByID[pixelId].userData`. This is the same object the SDK
 * reads when building the request payload, so updating it before trackSingle ensures
 * the advanced matching parameters are included in the event.
 */
function refreshAdvancedMatching() {
  const segmentTraits = analytics.userTraits();
  if (Object.keys(segmentTraits).length === 0) {
    return;
  }

  const formattedTraits = formatTraits(segmentTraits);

  const fbq = typedWindow.fbq;
  const pixel = fbq?.instance?.pixelsByID?.[FB_S3_COLLAR_PIXEL_ID];
  if (pixel) {
    pixel.userData = { ...pixel.userData, ...formattedTraits };
  }
}

/**
 * Map Segment e-commerce event properties to Facebook Pixel event properties.
 * Uses productFamilyName (e.g. "Series 3", "Mini") as content_id instead of SKU
 * to consolidate FB's optimization signal across color/size variants.
 */
function fbProductEventProperties(properties: SegmentProduct): FbEventProperties {
  // Since this pixel only cares about tracked collar kit SKUs, we'll lump in the price of the subscription with the
  // collar kit. Use analytics price when available — represents expected LTV for marketing pixels.
  const subscriptionValue = properties.analyticsSubscriptionValue ?? properties.subscriptionPrice ?? 0;
  const totalValue = Number(properties.price) + Number(subscriptionValue);
  const contentId = properties.catalogProductId ?? properties.productFamilyName ?? properties.id;

  return {
    content_ids: [contentId],
    content_type: 'product',
    content_name: properties.name,
    currency: properties.currency,
    value: totalValue.toFixed(2),
    contents: [
      {
        id: contentId,
        quantity: properties.quantity,
        item_price: totalValue.toFixed(2),
      },
    ],
  };
}

/**
 * Map Segment e-commerce cart event properties (multiple products) to Facebook Pixel event properties.
 * Filters out subscription products and uses productFamilyName as content_id instead of SKU
 * (or the free trial content_id when the collar was purchased with a free trial plan).
 * Deduplicates content_ids since multiple SKU variants may map to the same family name.
 */
function fbCartEventProperties(
  properties: SegmentCartEventProperties | SegmentPurchaseEventProperties,
): FbEventProperties {
  const fbEventProperties: FbEventProperties = {};

  let total = 0;
  const productsAndVariantsBySku = getProductsAndVariantsBySku(store.getState().config.products);

  // Filter to tracked marketing products, then keep only those with a productFamilyName (drops subscriptions)
  const collarProducts: SegmentProduct[] = properties.products
    .filter((product) => isTrackedMarketingProduct(product, productsAndVariantsBySku))
    .filter((product) => product.productFamilyName);

  // Deduplicate by productFamilyName — multiple collar SKUs (e.g. different colors) map to the same family
  const contentIds: string[] = [];
  const contents: { id: string; quantity: number; item_price: string }[] = [];

  for (const product of collarProducts) {
    const contentId = product.freeTrial
      ? FB_FREE_TRIAL_CONTENT_ID
      : (product.catalogProductId ?? product.productFamilyName!);
    const subscriptionValue = product.analyticsSubscriptionValue ?? product.subscriptionPrice ?? 0;
    const itemPrice = Number(subscriptionValue).toFixed(2);

    const existing = contents.find((c) => c.id === contentId && c.item_price === itemPrice);
    if (existing) {
      existing.quantity += product.quantity;
    } else {
      contents.push({
        id: contentId,
        quantity: product.quantity,
        item_price: itemPrice,
      });
      contentIds.push(contentId);
    }

    total += subscriptionValue * product.quantity;
  }

  fbEventProperties.content_ids = contentIds;
  fbEventProperties.contents = contents;
  fbEventProperties.num_items = collarProducts.length;

  if (total) {
    fbEventProperties.value = Number(total).toFixed(2);
    fbEventProperties.currency = properties.currency;
  }

  return fbEventProperties;
}

function fbProductViewed(messageId: string, properties: SegmentProduct) {
  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'ViewContent', fbProductEventProperties(properties), {
    eventID: messageId,
  });
}

function fbProductAdded(messageId: string, properties: SegmentProduct) {
  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'AddToCart', fbProductEventProperties(properties), {
    eventID: messageId,
  });
}

function fbCheckoutStarted(messageId: string, properties: SegmentCartEventProperties) {
  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'InitiateCheckout', fbCartEventProperties(properties), {
    eventID: messageId,
  });
}

function fbShippingCompleted(messageId: string, properties: SegmentCartEventProperties) {
  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'CompleteRegistration', fbCartEventProperties(properties), {
    eventID: messageId,
  });
}

function fbPaymentInfoEntered(messageId: string) {
  fbPixel(
    'trackSingle',
    FB_S3_COLLAR_PIXEL_ID,
    'AddPaymentInfo',
    {},
    {
      eventID: messageId,
    },
  );
}

function fbOrderCompleted(messageId: string, properties: SegmentPurchaseEventProperties) {
  const fbProperties = fbCartEventProperties(properties);

  // order_id enables conversion attribution and deduplication in Facebook Events Manager
  // https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/custom-data#order-id
  fbProperties.order_id = properties.orderId;

  fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'Purchase', fbProperties, {
    eventID: messageId,
  });

  // Custom purchase event to distinguish DTC vs GWP purchases for separate conversion tracking
  const customEventName = properties.isGWP ? 'Purchases - GWP' : 'Purchases - DTC';
  fbPixel('trackSingleCustom', FB_S3_COLLAR_PIXEL_ID, customEventName, fbProperties, {
    eventID: `${messageId}-custom`,
  });

  // Meta standard trial event so free trial checkouts are visible as their own conversion in Events Manager
  if (properties.products.some((product) => product.freeTrial)) {
    fbPixel('trackSingle', FB_S3_COLLAR_PIXEL_ID, 'StartTrial', fbProperties, {
      eventID: `${messageId}-trial`,
    });
  }
}
